import { EventEmitter } from '../../utils/event-emitter.js';
import { BaseService } from '../base-service.js';
import { tokenProvider } from '../auth/token-provider.js';

export class FileProcessor extends BaseService {
  constructor() {
    super();
    this.basePath = '/documents';
    // Removed hardcoded base URL override
    this.events = new EventEmitter();
    
    // supported file types
    this.supportedTypes = new Set([
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/rtf',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.presentation',
      'application/x-iwork-pages-sffpages',
      'application/x-iwork-numbers-sffnumbers',
      'application/x-iwork-keynote-sffkey',
      'text/markdown',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ]);
  }

  // Delegate event emitter methods
  on(event, callback) {
    return this.events.on(event, callback);
  }

  off(event, callback) {
    return this.events.off(event, callback);
  }

  emit(event, data) {
    return this.events.emit(event, data);
  }

  getSessionInfo() {
    const sessionData = tokenProvider.getSessionData();
    if (!sessionData?.orgId || !sessionData?.userData?.id) {
      throw new Error('Missing required session data');
    }
    return { orgId: sessionData.orgId, userId: sessionData.userData.id };
  }

  async processFile(file) {
    try {
      if (!this.isSupportedFileType(file)) {
        throw new Error(`Unsupported file type: ${file.type}`);
      }

      // Create document first
      const document = await this.createDocument(file);

      // Then process it
      const formData = new FormData();
      formData.append('file', file);

      const { orgId, userId } = this.getSessionInfo();
      const response = await fetch(`${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/process/${document.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await tokenProvider.getToken()}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to process file');
      }

      const result = await response.json();
      return {
        id: document.id,
        status: result.status,
        jobId: result.jobId,
        metadata: {
          originalName: file.name,
          mimeType: file.type,
          size: file.size
        }
      };
    } catch (error) {
      console.error('Error processing file:', error);
      throw error;
    }
  }

  async createDocument(file) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}`, {
        title: file.name,
        mimeType: file.type,
        size: file.size
      });

      console.log('[file-processor] createDocument response', response);
      
      // The response is already a parsed object from BaseService
      if (!response || !response.id) {
        throw new Error('Failed to create document - invalid response');
      }

      return response;
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  }

  async pollDocumentStatus(documentId, maxAttempts = 30, interval = 2000) {
    let attempts = 0;
    const poll = async () => {
      try {
        const status = await this.getDocumentStatus(documentId);
        
        switch (status) {
          case 'processed':
            return status;
          case 'error':
            throw new Error('Document processing failed');
          case 'pending':
          case 'processing':
            if (attempts >= maxAttempts) {
              throw new Error('Document processing timed out');
            }
            attempts++;
            await new Promise(resolve => setTimeout(resolve, interval));
            return poll();
          default:
            throw new Error(`Unknown document status: ${status}`);
        }
      } catch (error) {
        console.error('Error polling document status:', error);
        throw error;
      }
    };

    return poll();
  }

  isSupportedFileType(file) {
    // Check exact mime type first
    if (this.supportedTypes.has(file.type)) {
      return true;
    }

    // Check file extension as fallback
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    const supportedExtensions = [
      '.pdf', '.doc', '.docx', '.ppt', '.pptx', '.txt', '.rtf',
      '.odt', '.ods', '.odp', '.pages', '.numbers', '.key', '.md',
      '.xlsx', '.xls', '.csv'
    ];

    return supportedExtensions.includes(extension);
  }

  async getDocumentStatus(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.get(`/${orgId}/users/${userId}/document/${documentId}/processing-status`);
      
      // Response is already parsed by BaseService
      if (!response || !response.status) {
        throw new Error('Failed to get document status - invalid response');
      }
      
      return response.status;
    } catch (error) {
      console.error('Error getting document status:', error);
      throw error;
    }
  }

  async getDocument(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.get(`/${orgId}/users/${userId}/document/${documentId}`);
      return response.data || response;
    } catch (error) {
      console.error('Error getting document:', error);
      throw error;
    }
  }
}

export const fileProcessor = new FileProcessor(); 